import {ActionIcon, Box, Button, Group, Loader, Paper, Select, Stack, Text} from '@mantine/core'
import {IconTruck, IconUsers} from '@tabler/icons-react'
import clsx from 'clsx'
import {useMemo} from 'react'

import type {RentalAssetType} from 'src/assets/rental-assets/schemas'
import type {WorkerType} from 'src/assets/worker-types/schemas'
import {formatPrice} from 'src/lib/format-price'

import {
    itemSubtotal,
    lineExtended,
    QUOTE_REQUIREMENT_KIND_LABELS,
    type QuoteItemLine,
    type QuoteLine,
    type QuoteRentalPeriod,
    type QuoteRequirement,
    type QuoteRequirementKind,
    unitSingularLabel,
} from './quote-builder-logic'
import {QuoteQuantityStepper} from './quote-quantity-stepper'
import classes from './quote-requirements-by-line.module.css'
import {
    addRequirement,
    isLineMapped,
    removeRequirement,
    requirementCount,
    requirementsForLine,
    setRequirementType,
    updateRequirementQty,
} from './quote-requirements-logic'

type QuoteRequirementsByLineProps = {
    lines: QuoteLine[]
    requirements: QuoteRequirement[]
    workerTypes: WorkerType[]
    rentalAssetTypes: RentalAssetType[]
    readOnly?: boolean
    onRequirementsChange: (requirements: QuoteRequirement[]) => void
    rentalPeriod?: QuoteRentalPeriod | null
}

function usedTypeIdsOnLine(
    lineRequirements: QuoteRequirement[],
    requirement: QuoteRequirement,
    kind: QuoteRequirementKind,
): Set<string> {
    const ids = new Set<string>()

    for (const lineRequirement of lineRequirements) {
        if (lineRequirement.id === requirement.id || lineRequirement.kind !== kind) {
            continue
        }

        if (kind === 'worker' && lineRequirement.workerTypeId.length > 0) {
            ids.add(lineRequirement.workerTypeId)
        }

        if (kind === 'equipment' && lineRequirement.rentalAssetTypeId.length > 0) {
            ids.add(lineRequirement.rentalAssetTypeId)
        }
    }

    return ids
}

function requirementTypeOptions(
    requirement: QuoteRequirement,
    lineRequirements: QuoteRequirement[],
    workerTypes: WorkerType[],
    rentalAssetTypes: RentalAssetType[],
) {
    const selectedTypeId = requirementTypeValue(requirement)

    if (requirement.kind === 'worker') {
        const usedTypeIds = usedTypeIdsOnLine(lineRequirements, requirement, 'worker')

        return workerTypes
            .filter((type) => !usedTypeIds.has(type.id) || type.id === selectedTypeId)
            .map((type) => ({value: type.id, label: type.name}))
    }

    const usedTypeIds = usedTypeIdsOnLine(lineRequirements, requirement, 'equipment')

    return rentalAssetTypes
        .filter(
            (type) =>
                type.archivedAt == null || type.id === selectedTypeId || !usedTypeIds.has(type.id),
        )
        .filter((type) => !usedTypeIds.has(type.id) || type.id === selectedTypeId)
        .map((type) => ({value: type.id, label: type.name}))
}

function requirementTypeValue(requirement: QuoteRequirement): string | null {
    if (requirement.kind === 'worker') {
        return requirement.workerTypeId || null
    }

    return requirement.rentalAssetTypeId || null
}

function requirementSummaryLabel(count: number, mapped: boolean): string {
    if (!mapped) {
        return 'no requirements yet'
    }

    return count === 1 ? '1 requirement' : `${count} requirements`
}

export function QuoteRequirementsByLine({
    lines,
    requirements,
    workerTypes,
    rentalAssetTypes,
    readOnly = false,
    onRequirementsChange,
    rentalPeriod = null,
}: QuoteRequirementsByLineProps) {
    const itemTotal = useMemo(() => itemSubtotal(lines, rentalPeriod), [lines, rentalPeriod])
    const itemLines = useMemo(
        () => lines.filter((line): line is QuoteItemLine => line.kind === 'item'),
        [lines],
    )

    function handleAddRequirement(lineId: string, kind: QuoteRequirementKind) {
        onRequirementsChange(addRequirement(requirements, lineId, kind))
    }

    if (readOnly && workerTypes.length === 0 && rentalAssetTypes.length === 0) {
        return (
            <Group justify="center" py="xl">
                <Loader aria-label="Loading requirement types" />
            </Group>
        )
    }

    return (
        <Paper withBorder radius="md" p="md">
            <Stack gap="lg">
                <Stack gap={4}>
                    <Text fw={600}>Requirements by line</Text>
                    <Text size="xs" c="dimmed">
                        The mapping is the layout — each priced line owns the Workers and Equipment
                        Ops must field beneath it.
                    </Text>
                </Stack>

                {itemLines.map((line) => {
                    const lineRequirements = requirementsForLine(requirements, line.id)
                    const mapped = isLineMapped(requirements, line.id)
                    const count = requirementCount(requirements, line.id)
                    const extended = lineExtended(line, itemTotal, rentalPeriod)

                    return (
                        <Box
                            key={line.id}
                            style={{
                                border: '1px solid var(--mantine-color-gray-3)',
                                borderRadius: 'var(--mantine-radius-md)',
                                overflow: 'hidden',
                            }}
                        >
                            <div className={classes['lineHeader']}>
                                <Group align="flex-start" gap="sm" wrap="nowrap">
                                    <Box
                                        aria-hidden="true"
                                        className={clsx(
                                            classes['statusDot'],
                                            mapped
                                                ? classes['statusDotMapped']
                                                : classes['statusDotUnmapped'],
                                        )}
                                    />
                                    <Stack gap={2}>
                                        <Text fw={600}>{line.description}</Text>
                                        <Text size="sm" c="dimmed">
                                            Qty {line.quantity.toLocaleString('en-US')}
                                            {line.duration != null && (
                                                <>
                                                    {' '}
                                                    × Duration{' '}
                                                    {line.duration.toLocaleString('en-US')}
                                                </>
                                            )}{' '}
                                            × {formatPrice(line.rate)} /{' '}
                                            {unitSingularLabel(line.unit)} ·{' '}
                                            {requirementSummaryLabel(count, mapped)}
                                        </Text>
                                    </Stack>
                                </Group>
                                <Text fw={600}>{formatPrice(extended)}</Text>
                            </div>

                            <div className={classes['lineBody']}>
                                {lineRequirements.map((requirement) => (
                                    <div key={requirement.id} className={classes['requirementRow']}>
                                        <span className={classes['iconChip']} aria-hidden="true">
                                            {requirement.kind === 'worker' ? (
                                                <IconUsers size={18} />
                                            ) : (
                                                <IconTruck size={18} />
                                            )}
                                        </span>

                                        <Select
                                            label={`${QUOTE_REQUIREMENT_KIND_LABELS[requirement.kind]} type`}
                                            labelProps={{style: {display: 'none'}}}
                                            placeholder={`Select ${QUOTE_REQUIREMENT_KIND_LABELS[requirement.kind].toLowerCase()} type`}
                                            data={requirementTypeOptions(
                                                requirement,
                                                lineRequirements,
                                                workerTypes,
                                                rentalAssetTypes,
                                            )}
                                            value={requirementTypeValue(requirement)}
                                            onChange={(value) => {
                                                if (value == null || readOnly) {
                                                    return
                                                }

                                                onRequirementsChange(
                                                    setRequirementType(
                                                        requirements,
                                                        requirement.id,
                                                        value,
                                                    ),
                                                )
                                            }}
                                            searchable
                                            disabled={readOnly}
                                            className={classes['typeSelect']}
                                        />

                                        <div className={classes['controls']}>
                                            <QuoteQuantityStepper
                                                value={requirement.qty}
                                                ariaLabel="Requirement quantity"
                                                disabled={readOnly}
                                                onChange={(qty) =>
                                                    onRequirementsChange(
                                                        updateRequirementQty(
                                                            requirements,
                                                            requirement.id,
                                                            qty,
                                                        ),
                                                    )
                                                }
                                            />

                                            <ActionIcon
                                                variant="subtle"
                                                color="gray"
                                                size="sm"
                                                aria-label="Remove requirement"
                                                disabled={readOnly}
                                                onClick={() =>
                                                    onRequirementsChange(
                                                        removeRequirement(
                                                            requirements,
                                                            requirement.id,
                                                        ),
                                                    )
                                                }
                                            >
                                                <Text span size="sm" c="dimmed" lh={1}>
                                                    ×
                                                </Text>
                                            </ActionIcon>
                                        </div>
                                    </div>
                                ))}

                                {!readOnly ? (
                                    <Group gap="sm">
                                        <Button
                                            variant="default"
                                            size="xs"
                                            style={{borderStyle: 'dashed'}}
                                            onClick={() => handleAddRequirement(line.id, 'worker')}
                                        >
                                            + Worker
                                        </Button>
                                        <Button
                                            variant="default"
                                            size="xs"
                                            style={{borderStyle: 'dashed'}}
                                            onClick={() =>
                                                handleAddRequirement(line.id, 'equipment')
                                            }
                                        >
                                            + Equipment
                                        </Button>
                                    </Group>
                                ) : null}
                            </div>
                        </Box>
                    )
                })}
            </Stack>
        </Paper>
    )
}
